import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Users, Inbox, ArrowRight, Activity, CalendarDays } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { greeting, timeAgo, ACTION_LABELS } from "@/lib/constants";
import { ENVIRONMENTS, ENV_ORDER, envKeyOf, tint } from "@/lib/environments";
import { SpaceAvatar } from "@/components/Avatars";
import { EnvBadge } from "@/components/EnvBadge";
import { CreateSpaceDialog } from "@/components/CreateSpaceDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data: home, isLoading } = useQuery({
    queryKey: ["home"],
    queryFn: async () => (await api.get("/home")).data,
  });

  const acceptInvitation = async (token) => {
    try {
      const { data } = await api.post(`/invitations/${token}/accept`);
      toast.success("Te has unido al Espacio");
      qc.invalidateQueries({ queryKey: ["home"] });
      navigate(`/space/${data.workspace_id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const workspaces = home?.workspaces || [];
  const invitations = home?.pending_invitations || [];
  const activity = home?.recent_activity || [];
  const budgetAlerts = home?.budget_alerts || [];
  const todayTasks = home?.today_summary?.today || [];
  const overdueTasks = home?.today_summary?.overdue || [];
  const taskSentence = () => {
    const n = todayTasks.length, o = overdueTasks.length;
    if (!n && !o) return null;
    const parts = [];
    if (n) parts.push(`${n} ${n === 1 ? "tarea" : "tareas"}`);
    if (o) parts.push(`${o} ${o === 1 ? "vencida" : "vencidas"}`);
    return `Hoy tienes ${parts.join(" y ")}.`;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-10" data-testid="home-page">
      <div className="animate-fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="home-greeting">
          {greeting(user?.name)} 👋
        </h1>
        <p className="text-sm text-muted-foreground mt-1 capitalize" data-testid="home-date">
          {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <p className="text-muted-foreground mt-2" data-testid="home-summary">
          {taskSentence() || (workspaces.length === 0
            ? "Crea tu primer Espacio para empezar a organizar tu mundo."
            : `Tienes ${workspaces.length} ${workspaces.length === 1 ? "Espacio" : "Espacios"}${invitations.length ? ` y ${invitations.length} ${invitations.length === 1 ? "invitación pendiente" : "invitaciones pendientes"}` : ""}.`)}
        </p>
      </div>

      {(todayTasks.length > 0 || overdueTasks.length > 0) && (
        <section className="space-y-3 animate-fade-up" data-testid="home-today-section">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Mis tareas de hoy</h2>
          </div>
          <Card className="divide-y divide-border/60">
            {[...overdueTasks, ...todayTasks].map((t) => {
              const overdue = overdueTasks.includes(t);
              return (
                <Link key={t.id} to={`/space/${t.workspace_id}/project/${t.project_id}?task=${t.id}`}
                  data-testid={`home-today-task-${t.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors group">
                  <span className={`h-2 w-2 rounded-full shrink-0 ${overdue ? "bg-destructive" : "bg-primary"}`} />
                  <span className="text-sm font-medium flex-1 truncate">{t.title}</span>
                  <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-[160px]">{t.project_icon} {t.project_name}</span>
                  {overdue ? (
                    <Badge variant="destructive" className="text-[10px] shrink-0">Vencida · {t.due_date}</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px] shrink-0">Hoy</Badge>
                  )}
                  <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </Link>
              );
            })}
          </Card>
        </section>
      )}

      {invitations.length > 0 && (
        <section id="inbox" className="space-y-3 animate-fade-up">
          <div className="flex items-center gap-2">
            <Inbox className="h-4 w-4 text-primary" />
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Bandeja de entrada</h2>
          </div>
          {invitations.map((inv) => (
            <Card key={inv.token} className="p-4 sm:p-5 flex items-center gap-4" data-testid={`invitation-card-${inv.token}`}>
              <SpaceAvatar icon={inv.workspace_icon} color={inv.workspace_color} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-semibold">{inv.invited_by_name}</span> te ha invitado a{" "}
                  <span className="font-semibold">{inv.workspace_name}</span>
                </p>
                <p className="text-xs text-muted-foreground">Rol: {inv.role_label}</p>
              </div>
              <Button size="sm" data-testid={`accept-invitation-btn-${inv.token}`} onClick={() => acceptInvitation(inv.token)}>
                Unirme
              </Button>
            </Card>
          ))}
        </section>
      )}

      {budgetAlerts.length > 0 && (
        <section className="space-y-2 animate-fade-up" data-testid="home-budget-alerts">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Presupuestos en alerta</h2>
          {budgetAlerts.map((b) => {
            const color = b.pct > 90 ? "#D93025" : "#F9AB00";
            return (
              <Link key={b.id} to={`/space/${b.workspace_id}/expenses?tab=budgets`} data-testid={`home-budget-alert-${b.id}`} className="block">
                <Card className="p-4 flex items-center gap-3 hover:border-primary/50 transition-colors">
                  <span className="font-heading font-bold text-lg shrink-0" style={{ color }}>{b.pct}%</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.name}</p>
                    <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-1">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(b.pct, 100)}%`, backgroundColor: color }} />
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{b.workspace_name}</span>
                </Card>
              </Link>
            );
          })}
        </section>
      )}

      <section className="space-y-3 animate-fade-up">
        <div className="flex items-center justify-between">
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Mis Espacios</h2>
          <Button variant="ghost" size="sm" data-testid="home-create-space-btn" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nuevo Espacio
          </Button>
        </div>
        {isLoading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        ) : workspaces.length === 0 ? (
          <Card className="p-10 text-center space-y-4 border-dashed">
            <div className="text-4xl">🪐</div>
            <div>
              <p className="font-heading font-semibold text-lg">Tu universo está vacío</p>
              <p className="text-sm text-muted-foreground mt-1">Un Espacio es el hogar de tu familia, empresa, viaje o club.</p>
            </div>
            <Button data-testid="empty-create-space-btn" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Crear mi primer Espacio
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {ENV_ORDER
              .map((key) => ({ key, env: ENVIRONMENTS[key], items: workspaces.filter((w) => envKeyOf(w) === key) }))
              .filter((g) => g.items.length > 0)
              .map(({ key, env, items }) => (
                <div key={key} className="space-y-3" data-testid={`home-env-group-${key}`}>
                  <Link to={`/spaces?env=${key}`} className="inline-flex items-center gap-2 group/env">
                    <span className="h-7 w-7 rounded-lg flex items-center justify-center text-sm" style={{ backgroundColor: tint(env.color, 0.2) }} aria-hidden>
                      {env.emoji}
                    </span>
                    <span className="font-heading font-bold text-sm group-hover/env:underline" style={{ color: env.text }}>{env.label}</span>
                    <span className="text-xs text-muted-foreground">{items.length}</span>
                  </Link>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {items.map((ws) => (
                      <Link key={ws.id} to={`/space/${ws.id}`} data-testid={`space-card-${ws.id}`}>
                        <Card className="p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_24px_rgba(59,74,101,0.10)] group cursor-pointer h-full">
                          <div className="flex items-start justify-between">
                            <SpaceAvatar icon={ws.icon} color={ws.color} size="lg" />
                            <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <p className="font-heading font-semibold text-lg mt-4 truncate">{ws.name}</p>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <EnvBadge ws={ws} />
                            <Badge variant="secondary" className="font-normal">{ws.my_role_label}</Badge>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Users className="h-3 w-3" /> {ws.member_count} {ws.member_count === 1 ? "miembro" : "miembros"}
                            </span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>

      {activity.length > 0 && (
        <section className="space-y-3 animate-fade-up">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Actividad reciente</h2>
          </div>
          <Card className="divide-y divide-border" data-testid="home-activity-feed">
            {activity.map((a) => (
              <div key={a.id} className="p-4 flex items-center gap-3 text-sm">
                <SpaceAvatar icon={a.workspace_icon} color={a.workspace_color} size="sm" />
                <p className="flex-1 min-w-0 truncate">
                  <span className="font-semibold">{a.actor_name}</span>{" "}
                  <span className="text-muted-foreground">{ACTION_LABELS[a.action] || a.action}</span>
                  {a.metadata?.name ? <span className="font-medium"> "{a.metadata.name}"</span> : null}
                  {a.metadata?.member_name ? <span className="font-medium"> ({a.metadata.member_name})</span> : null}
                </p>
                <span className="text-xs text-muted-foreground shrink-0">{timeAgo(a.created_at)}</span>
              </div>
            ))}
          </Card>
        </section>
      )}

      <CreateSpaceDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
